import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PoChartSerie, PoChartType, PoNotificationService, PoSelectOption, PoTableColumn } from '@po-ui/ng-components';
import { ConsolidatedRow } from '../../interfaces/consolidated-report';
import { IssueGoal } from '../../interfaces/issue';
import { Project } from '../../interfaces/project';
import { DailyReportListResult, DailyReportRow, ReportFilters, RuleReportRow } from '../../interfaces/reports';
import { SonarExtractorService } from '../../services/sonar-extractor.service';
import {
  buildCircularDailySeries,
  buildDailyChartData,
  buildDefaultEnd,
  buildDefaultStart,
  clearOrphanTooltips,
  exportChartToPng,
  formatUtcDate,
  isCircularChartType,
  SONAR_CHART_TYPE_OPTIONS_NO_CIRCULAR,
  startTooltipObserver,
  triggerDownload,
} from '../../utils/sonar-extractor.utils';

/** Identificadores das sub-abas de relatórios */
type ReportTab = 'byRule' | 'daily' | 'consolidated';

@Component({
  selector: "app-sonar-extractor-reports",
  templateUrl: "./sonar-extractor-reports.component.html",
  styleUrls: ["./sonar-extractor-reports.component.css"],
})
export class SonarExtractorReportsComponent implements OnInit, OnDestroy {

  // Opções de tipo de gráfico (provenientes das utils centralizadas)
  readonly chartOptionsDailyEvolution: PoSelectOption[] = SONAR_CHART_TYPE_OPTIONS_NO_CIRCULAR;

  projectOptions: PoSelectOption[] = [{ label: 'Todos', value: 'ALL' }];

  readonly severityOptions: PoSelectOption[] = [
    { label: "Todas",    value: "ALL" },
    { label: "Blocker",  value: "BLOCKER" },
    { label: "Critical", value: "CRITICAL" },
    { label: "Major",    value: "MAJOR" },
    { label: "Minor",    value: "MINOR" },
    { label: "Info",     value: "INFO" },
  ];

  // ─── Paginação ────────────────────────────────────────────────────────────────
  private readonly PAGE_LIMIT = 10;

  // Por Regra
  private byRulePage = 1;
  hasMoreByRule = false;

  // Diário
  private dailyPage = 1;
  hasMoreDaily = false;

  // Consolidado
  private consolidatedPage = 1;
  hasMoreConsolidated = false;

  // ─── Controle de carregamento lazy por aba ────────────────────────────────────
  private loadedTabs = new Set<ReportTab>();

  // ─── Filtros da aba "Por Regra" ───────────────────────────────────────────────
  filterByRulePeriod: { start: string; end: string } = { start: buildDefaultStart(), end: buildDefaultEnd() };
  filterByRuleProjectKey = "ALL";
  filterByRuleSeverity: IssueGoal | "ALL" = "ALL";

  // ─── Filtros da aba "Diário" ──────────────────────────────────────────────────
  filterDailyPeriod: { start: string; end: string } = { start: buildDefaultStart(), end: buildDefaultEnd() };
  filterDailySeverity: IssueGoal | "ALL" = "ALL";

  // ─── Filtros da aba "Consolidado" ─────────────────────────────────────────────
  filterConsolidatedPeriod: { start: string; end: string } = { start: buildDefaultStart(), end: buildDefaultEnd() };
  filterConsolidatedProjectKey = "ALL";
  filterConsolidatedSeverity: IssueGoal | "ALL" = "ALL";

  private tooltipObserver: MutationObserver | null = null;

  // Flag para forçar recriação do po-chart ao trocar tipo (evita acúmulo de tooltips)
  showDailyChart = true;

  // Seletores de tipo de gráfico
  private _dailyChartType: PoChartType = PoChartType.Line;
  get dailyChartType(): PoChartType { return this._dailyChartType; }
  set dailyChartType(value: PoChartType) {
    if (this._dailyChartType === value) { return; }
    this._dailyChartType = value;
    this.resetChart();
  }

  /** Força recriação do gráfico e limpa tooltips órfãos */
  private resetChart(): void {
    clearOrphanTooltips();
    this.showDailyChart = false;
    setTimeout(() => (this.showDailyChart = true), 0);
  }

  // ─── Getters: adapta séries ao tipo de gráfico selecionado ───────────────────

  get dailyChartDisplaySeries(): PoChartSerie[] {
    return isCircularChartType(this.dailyChartType)
      ? buildCircularDailySeries(this.dailyRows)
      : this.dailyChartSeries;
  }

  get dailyChartDisplayCategories(): string[] {
    return isCircularChartType(this.dailyChartType) ? [] : this.dailyChartCategories;
  }

  // Por Regra
  loadingByRule = false;
  exportingByRuleCsv = false;
  ruleRows: RuleReportRow[] = [];
  readonly ruleColumns: PoTableColumn[] = [
    { property: "rule_key",     label: "Chave da Regra",  width: "130px" },
    { property: "rule_name",    label: "Regra",           width: "250px" },
    { property: "project_name", label: "Projeto",         width: "200px" },
    { property: "issues_total", label: "Ocorrências",     type: "number", width: "120px" },
  ];

  // Diário
  loadingDaily = false;
  dailyRows: DailyReportRow[] = [];
  dailyChartCategories: string[] = [];
  dailyChartSeries: PoChartSerie[] = [];
  readonly dailyColumns: PoTableColumn[] = [
    { property: "snapshot_date", label: "Data",      width: "130px" },
    { property: "blocker",       label: "Blocker",   type: "number", width: "90px"  },
    { property: "critical",      label: "Critical",  type: "number", width: "90px"  },
    { property: "major",         label: "Major",     type: "number", width: "90px"  },
    { property: "minor",         label: "Minor",     type: "number", width: "90px"  },
    { property: "info",          label: "Info",      type: "number", width: "90px"  },
    { property: "issues_total",  label: "Total",     type: "number", width: "90px"  },
  ];

  // Consolidado
  loadingConsolidated = false;
  exportingConsolidatedCsv = false;
  downloadingDailyChart = false;
  consolidatedRows: ConsolidatedRow[] = [];
  readonly consolidatedColumns: PoTableColumn[] = [
    { property: "project_name", label: "Projeto",       width: "100px"            },
    { property: "rule_name",    label: "Regra",         width: "100px"            },
    { property: "blocker",      label: "Blocker",       type: "number", width: "75px" },
    { property: "critical",     label: "Critical",      type: "number", width: "75px" },
    { property: "major",        label: "Major",         type: "number", width: "70px" },
    { property: "minor",        label: "Minor",         type: "number", width: "70px" },
    { property: "info",         label: "Info",          type: "number", width: "70px" },
    { property: "issues_total", label: "Total",         type: "number", width: "70px" },
  ];

  constructor(
    private sonarService: SonarExtractorService,
    private notification: PoNotificationService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.tooltipObserver = startTooltipObserver();
    this.loadProjects();
    // Carrega apenas a primeira aba (Por Regra) no primeiro acesso.
    // Adia para após o primeiro ciclo de change detection do Angular,
    // evitando NG0100 (ExpressionChangedAfterItHasBeenCheckedError).
    setTimeout(() => this.loadTabIfNeeded('byRule'));
  }

  ngOnDestroy(): void {
    this.tooltipObserver?.disconnect();
    clearOrphanTooltips();
  }

  loadProjects(): void {
    this.sonarService.getProjects()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (projects: Project[]) => {
        const list = Array.isArray(projects) ? projects : [];
        this.projectOptions = [
          { label: "Todos", value: "ALL" },
          ...list.map((p) => ({ label: p.name, value: p.sonar_key })),
        ];
      },
      error: () => this.notification.error("Erro ao carregar a lista de projetos."),
    });
  }

  // ─── Controle de abas e lazy loading ─────────────────────────────────────────

  /** Chamado pelo evento (p-click) de cada po-tab */
  onTabClick(tabId: ReportTab): void {
    this.loadTabIfNeeded(tabId);
  }

  /** Carrega os dados da aba somente se ainda não foram carregados */
  private loadTabIfNeeded(tabId: ReportTab): void {
    if (this.loadedTabs.has(tabId)) { return; }
    switch (tabId) {
      case 'byRule':       this.loadByRule();       break;
      case 'daily':        this.loadDaily();        break;
      case 'consolidated': this.loadConsolidated(); break;
    }
  }

  // ─── Aplicar filtros por aba ──────────────────────────────────────────────────

  applyByRuleFilters(): void {
    this.byRulePage = 1;
    this.loadByRule();
  }

  applyDailyFilters(): void {
    this.dailyPage = 1;
    this.loadDaily();
  }

  applyConsolidatedFilters(): void {
    this.consolidatedPage = 1;
    this.loadConsolidated();
  }

  // ─── Por Regra ───────────────────────────────────────────────────────────────

  loadByRule(append = false): void {
    // Usa os valores do filtro ou cai back para os 30 dias padrão, garantindo
    // que a chamada sempre seja feita mesmo que o po-datepicker-range tenha
    // redefinido o ngModel para '' durante a inicialização do componente.
    const startDate = this.filterByRulePeriod?.start || buildDefaultStart();
    const endDate   = this.filterByRulePeriod?.end   || buildDefaultEnd();

    this.loadingByRule = true;
    const filters: Required<Pick<ReportFilters, 'startDate' | 'endDate'>> & ReportFilters = {
      startDate,
      endDate,
      projectKey: this.filterByRuleProjectKey !== "ALL" ? this.filterByRuleProjectKey : undefined,
      severity:   this.filterByRuleSeverity !== "ALL" ? (this.filterByRuleSeverity as IssueGoal) : undefined,
      page:       this.byRulePage,
      limit:      this.PAGE_LIMIT,
    };
    this.sonarService.getReportByRule(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.ruleRows = append ? [...this.ruleRows, ...rows] : rows;
          this.hasMoreByRule = rows.length === this.PAGE_LIMIT;
          this.loadingByRule = false;
          this.loadedTabs.add('byRule');
        },
        error: () => {
          this.notification.error("Erro ao carregar o relatório por regra.");
          this.loadingByRule = false;
        },
      });
  }

  loadMoreByRule(): void {
    this.byRulePage++;
    this.loadByRule(true);
  }

  exportByRuleCsv(): void {
    this.exportingByRuleCsv = true;
    const filters: ReportFilters = {
      startDate:  this.filterByRulePeriod.start || undefined,
      endDate:    this.filterByRulePeriod.end   || undefined,
      projectKey: this.filterByRuleProjectKey !== "ALL" ? this.filterByRuleProjectKey : undefined,
      severity:   this.filterByRuleSeverity !== "ALL" ? (this.filterByRuleSeverity as IssueGoal) : undefined,
    };
    this.sonarService.exportReportByRuleCsv(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: blob => {
        triggerDownload(blob, 'relatorio-por-regra.csv');
        this.exportingByRuleCsv = false;
      },
      error: () => {
        this.notification.error('Erro ao exportar o relatório por regra.');
        this.exportingByRuleCsv = false;
      },
    });
  }

  // ─── Diário ──────────────────────────────────────────────────────────────────

  loadDaily(append = false): void {
    if (!this.filterDailyPeriod.start || !this.filterDailyPeriod.end) {
      this.notification.warning("Selecione um período para carregar o relatório diário.");
      return;
    }
    this.loadingDaily = true;
    const filters: Required<Pick<ReportFilters, 'startDate' | 'endDate'>> & ReportFilters = {
      startDate: this.filterDailyPeriod.start,
      endDate:   this.filterDailyPeriod.end,
      severity:  this.filterDailySeverity !== "ALL" ? (this.filterDailySeverity as IssueGoal) : undefined,
      page:      this.dailyPage,
      limit:     this.PAGE_LIMIT,
    };
    this.sonarService.getReportDaily(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (result: DailyReportListResult) => {
        const formatted = result.rows.map(r => ({ ...r, snapshot_date: formatUtcDate(r.snapshot_date) }));
        this.dailyRows = append ? [...this.dailyRows, ...formatted] : formatted;
        this.hasMoreDaily = this.dailyPage < result.total_pages;
        this.buildDailyChart(this.dailyRows);
        this.loadingDaily = false;
        this.loadedTabs.add('daily');
      },
      error: () => {
        this.notification.error("Erro ao carregar o relatório diário.");
        this.loadingDaily = false;
      },
    });
  }

  loadMoreDaily(): void {
    this.dailyPage++;
    this.loadDaily(true);
  }

  downloadDailyChartPng(): void {
    this.downloadingDailyChart = true;
    exportChartToPng('reports-daily-chart', 'relatorio-diario.png', msg => this.notification.warning(msg))
      .finally(() => (this.downloadingDailyChart = false));
  }

  // ─── Consolidado ─────────────────────────────────────────────────────────────

  loadConsolidated(append = false): void {
    this.loadingConsolidated = true;
    const filters: ReportFilters = {
      startDate:  this.filterConsolidatedPeriod.start || undefined,
      endDate:    this.filterConsolidatedPeriod.end   || undefined,
      projectKey: this.filterConsolidatedProjectKey !== "ALL" ? this.filterConsolidatedProjectKey : undefined,
      severity:   this.filterConsolidatedSeverity !== "ALL" ? (this.filterConsolidatedSeverity as IssueGoal) : undefined,
      page:       this.consolidatedPage,
      limit:      this.PAGE_LIMIT,
    };
    this.sonarService.getConsolidatedReport(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (data) => {
        this.consolidatedRows = append ? [...this.consolidatedRows, ...data] : data;
        this.hasMoreConsolidated = data.length === this.PAGE_LIMIT;
        this.loadingConsolidated = false;
        this.loadedTabs.add('consolidated');
      },
      error: () => {
        this.notification.error("Erro ao carregar o relatório consolidado.");
        this.loadingConsolidated = false;
      },
    });
  }

  loadMoreConsolidated(): void {
    this.consolidatedPage++;
    this.loadConsolidated(true);
  }

  exportConsolidatedCsv(): void {
    this.exportingConsolidatedCsv = true;
    const filters: ReportFilters = {
      startDate:  this.filterConsolidatedPeriod.start || undefined,
      endDate:    this.filterConsolidatedPeriod.end   || undefined,
      projectKey: this.filterConsolidatedProjectKey !== "ALL" ? this.filterConsolidatedProjectKey : undefined,
      severity:   this.filterConsolidatedSeverity !== "ALL" ? (this.filterConsolidatedSeverity as IssueGoal) : undefined,
    };
    this.sonarService.exportConsolidatedCsv(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: blob => {
        triggerDownload(blob, 'relatorio-consolidado.csv');
        this.exportingConsolidatedCsv = false;
      },
      error: () => {
        this.notification.error('Erro ao exportar o relatório consolidado.');
        this.exportingConsolidatedCsv = false;
      },
    });
  }

  // ─── Helpers privados (delegam para utils centralizadas) ─────────────────────

  private buildDailyChart(rows: DailyReportRow[]): void {
    const { categories, series } = buildDailyChartData(rows);
    this.dailyChartCategories = categories;
    this.dailyChartSeries     = series;
  }
}
